import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { Section, MediaItem, prefetchSectionImages } from '../../utils/mediaHelpers';

const sectionUrl = (path: string) => `/${path}`;

const css = `
  @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;600&family=Inter:wght@300;400;500&display=swap');
  * { box-sizing: border-box; }

  @keyframes fadeUp {
    from { opacity: 0; transform: translateY(14px); }
    to   { opacity: 1; transform: translateY(0); }
  }

  .page-wrap {
    min-height: 100vh;
    background-color: #080808;
    padding: 80px 48px 80px;
    font-family: 'Montserrat', sans-serif;
  }

  .card {
    padding: 28px 12px;
    border: 1px solid #181818;
    border-radius: 3px;
    background: #0d0d0d;
    color: #6a6a6a;
    font-family: 'Montserrat', sans-serif;
    font-weight: 600;
    font-size: 0.75rem;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    cursor: pointer;
    text-align: center;
    transition: border-color 0.25s ease, color 0.25s ease, background 0.25s ease;
    user-select: none;
    will-change: transform;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .card:hover {
    border-color: #4db0f2;
    color: #fff;
    background: #0f0f0f;
  }

  .back-btn {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    background: none;
    border: none;
    color: #a6d7f8;
    font-family: 'Montserrat', sans-serif;
    font-size: 0.62rem;
    letter-spacing: 0.2em;
    font-weight: 600;
    text-transform: uppercase;
    cursor: pointer;
    padding: 0;
    transition: color 0.2s ease;
  }

  .back-btn:hover { color: #fff; }
  .back-btn:hover svg { transform: translateX(-3px); }
  .back-btn svg { transition: transform 0.2s ease; }

  .sibling-btn {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    background: none;
    border: 1px solid #181818;
    border-radius: 3px;
    color: #6a6a6a;
    font-family: 'Montserrat', sans-serif;
    font-size: 0.6rem;
    letter-spacing: 0.16em;
    font-weight: 600;
    text-transform: uppercase;
    cursor: pointer;
    padding: 8px 14px;
    transition: border-color 0.2s ease, color 0.2s ease;
    text-decoration: none;
    white-space: nowrap;
    max-width: 160px;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .sibling-btn:hover {
    border-color: #4db0f2;
    color: #fff;
  }

  .eyebrow {
    font-family: 'Montserrat', sans-serif;
    font-size: 0.58rem;
    font-weight: 600;
    letter-spacing: 0.28em;
    color: #4db0f2;
    text-transform: uppercase;
    margin: 0 0 12px;
  }

  .heading {
    font-family: 'Azonix', sans-serif;
    font-size: 2.2rem;
    color: #fff;
    margin: 0;
    letter-spacing: 0.06em;
    text-transform: uppercase;
  }

  .divider {
    width: 32px;
    height: 2px;
    background: #4db0f2;
    margin-top: 16px;
  }

  .tile-label {
    font-family: 'Azonix', sans-serif;
    font-size: 1.1rem;
    color: #fff;
    letter-spacing: 0.12em;
    text-shadow: 0 2px 16px rgba(0,0,0,0.8);
  }

  @media (max-width: 768px) {
    .page-wrap { padding: 60px 16px 60px !important; }
    .heading { font-size: 1.6rem !important; }
    .tile-label { font-size: 0.85rem !important; }
    .sibling-btn { max-width: 110px; font-size: 0.55rem; padding: 8px 10px; }
  }
`;

const getColCount = (n: number): number => {
  if (n <= 3) return n;
  if (n === 4) return 2;
  for (let c = 5; c >= 2; c--) {
    if (n % c === 0) return c;
  }
  return 3;
};

const tileCols = (n: number) => {
  if (n === 3) return { cols: 3 };
  return { cols: 2 };
};

const LandingPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [hierarchical, setHierarchical] = useState<Record<string, Section[]>>({});
  const [topLevel, setTopLevel]         = useState<Section[]>([]);
  const [prefetched, setPrefetched]     = useState<Record<string, Record<string, MediaItem | null>>>({});
  const [active, setActive]             = useState<Section | null>(null);
  const [animKey, setAnimKey]           = useState(0);
  const [hoveredId, setHoveredId]       = useState<string | null>(null);
  const [hoveredTileId, setHoveredTileId] = useState<string | null>(null);
  const [isMobile, setIsMobile]         = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handle = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handle);
    return () => window.removeEventListener('resize', handle);
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/sections`);
        const filtered: Section[] = (data as Section[]).filter((s) => !s.hidden);
        const grouped = filtered.reduce((acc, s) => {
          const k = s.parent_id || '0';
          if (!acc[k]) acc[k] = [];
          acc[k].push(s);
          return acc;
        }, {} as Record<string, Section[]>);

        setHierarchical(grouped);
        setTopLevel(grouped['0'] || []);

        (grouped['0'] || [])
          .filter((s) => grouped[s.id]?.length)
          .forEach(async (parent) => {
            const imgs = await prefetchSectionImages(grouped[parent.id] || []);
            setPrefetched((prev) => ({ ...prev, [parent.id]: imgs }));
          });
      } catch (e) { console.error(e); }
    })();
  }, []);

  // Sync active section from location state so browser back/forward works
  useEffect(() => {
    const activeId = (location.state as any)?.activeId;
    if (activeId && topLevel.length > 0) {
      const section = topLevel.find((s) => s.id === activeId) ?? null;
      setActive(section);
      setAnimKey((k) => k + 1);
    } else if (!activeId) {
      setActive(null);
      setAnimKey((k) => k + 1);
    }
  }, [location.state, topLevel]);

  const handleClick = (s: Section) => {
    if (hierarchical[s.id]?.length) {
      // Push a new history entry with the section ID — browser back will clear it
      navigate('/work', { state: { activeId: s.id } });
    } else {
      navigate(sectionUrl(s.path));
    }
  };

  const goBack = () => navigate(-1);

  const switchSection = (s: Section) => {
    navigate('/work', { state: { activeId: s.id } });
  };

  const children    = active ? (hierarchical[active.id] || []) : [];
  const images      = active ? (prefetched[active.id] || {}) : {};
  const imagesReady = active ? !!prefetched[active.id] : false;
  const { cols: tileCo } = tileCols(children.length);

  const topLevelIndex = active ? topLevel.findIndex((s) => s.id === active.id) : -1;
  const prevSection   = topLevelIndex > 0 ? topLevel[topLevelIndex - 1] : null;
  const nextSection   = topLevelIndex < topLevel.length - 1 ? topLevel[topLevelIndex + 1] : null;

  const colCount  = getColCount(topLevel.length);
  const gap       = 10;
  const cardWidth = `calc((100% - ${(colCount - 1) * gap}px) / ${colCount})`;

  return (
    <>
      <style>{css}</style>
      <div className="page-wrap">
        <div style={{ maxWidth: '1400px', margin: '0 auto' }}>

          {/* Back + prev/next */}
          <AnimatePresence>
            {active && (
              <motion.div
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '40px' }}
                initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
              >
                <button className="back-btn" onClick={goBack}>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M10 3L5 8L10 13" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Back
                </button>

                <div style={{ display: 'flex', gap: 8 }}>
                  {prevSection ? (
                    <button className="sibling-btn" onClick={() => switchSection(prevSection)}>
                      <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                        <path d="M10 3L5 8L10 13" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      {prevSection.name}
                    </button>
                  ) : (
                    <span className="sibling-btn" style={{ opacity: 0.2, cursor: 'not-allowed' }}>
                      <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                        <path d="M10 3L5 8L10 13" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      Prev
                    </span>
                  )}
                  {nextSection ? (
                    <button className="sibling-btn" onClick={() => switchSection(nextSection)}>
                      {nextSection.name}
                      <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                        <path d="M6 3L11 8L6 13" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </button>
                  ) : (
                    <span className="sibling-btn" style={{ opacity: 0.2, cursor: 'not-allowed' }}>
                      Next
                      <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                        <path d="M6 3L11 8L6 13" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </span>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Header */}
          <motion.div
            key={`hdr-${animKey}`}
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            style={{ marginBottom: '40px' }}
          >
            <p className="eyebrow">{active ? 'Select a category' : 'Select a section'}</p>
            <h1 className="heading">{active ? active.name : 'Work'}</h1>
            <div className="divider" />
          </motion.div>

          <AnimatePresence mode="wait">

            {/* ── Top level: text cards ── */}
            {!active && (
              <motion.div
                key="top"
                initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                style={{ display: 'flex', flexWrap: 'wrap', gap: `${gap}px`, justifyContent: 'center', overflow: 'visible' }}
              >
                {topLevel.map((s, i) => {
                  const isMobileWidth = typeof window !== 'undefined' && window.innerWidth < 600;
                  const mobileWidth = `calc(50% - ${gap / 2}px)`;
                  return (
                    <motion.div
                      key={s.id}
                      className="card"
                      style={{ width: isMobileWidth ? mobileWidth : cardWidth, position: 'relative', zIndex: hoveredId === s.id ? 10 : 1 }}
                      initial={{ opacity: 0, y: 14 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.2, delay: i * 0.04 }}
                      whileHover={{ scale: 1.05, boxShadow: '0 0 20px rgba(77,176,242,0.15), 0 10px 24px rgba(77,176,242,0.12)' }}
                      whileTap={{ scale: 0.97 }}
                      onHoverStart={() => setHoveredId(s.id)}
                      onHoverEnd={() => setHoveredId(null)}
                      onClick={() => handleClick(s)}
                    >
                      {s.name}
                    </motion.div>
                  );
                })}
              </motion.div>
            )}

            {/* ── Second level: image tiles ── */}
            {active && imagesReady && (
              <motion.div
                key={`tiles-${active.id}`}
                initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
                style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : `repeat(${tileCo}, 1fr)`, gap: '6px', width: '100%' }}
              >
                {children.map((child: Section, i: number) => {
                  const img = images[child.id];
                  return (
                    <motion.div
                      key={child.id}
                      style={{ position: 'relative', overflow: 'hidden', borderRadius: '3px', aspectRatio: tileCo === 3 ? '2/3' : '16/9', zIndex: hoveredTileId === child.id ? 10 : 1 }}
                      initial={{ opacity: 0, y: 16 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.2, delay: i * 0.06 }}
                      whileHover={{ scale: 1.02, boxShadow: '0 0 20px rgba(77,176,242,0.15), 0 10px 24px rgba(77,176,242,0.12)' }}
                      whileTap={{ scale: 0.97 }}
                      onHoverStart={() => setHoveredTileId(child.id)}
                      onHoverEnd={() => setHoveredTileId(null)}
                    >
                      <Link to={sectionUrl(child.path)} style={{ display: 'block', width: '100%', height: '100%' }}>
                        {img ? (
                          <motion.img
                            src={`https://${process.env.REACT_APP_S3_BUCKET}/${img.s3_key}`}
                            alt={child.name}
                            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', filter: 'brightness(0.7)' }}
                            whileHover={{ scale: 1.05 }}
                            transition={{ duration: 0.35, ease: 'easeInOut' }}
                          />
                        ) : (
                          <div style={{ width: '100%', height: '100%', background: '#0d0d0d', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <span style={{ color: '#222', fontSize: '0.65rem', letterSpacing: '0.15em', textTransform: 'uppercase' }}>No image</span>
                          </div>
                        )}

                        <motion.div
                          style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '100%', background: 'linear-gradient(to top, rgba(0,0,0,0.75) 0%, rgba(0,0,0,0.25) 35%, rgba(0,0,0,0) 65%)', zIndex: 10, pointerEvents: 'none' }}
                          initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                          transition={{ duration: 0.4, delay: 0.2 + i * 0.06 }}
                        />

                        <motion.div
                          className="tile-label"
                          style={{ position: 'absolute', bottom: 0, left: 0, right: 0, zIndex: 20, padding: '16px 20px', textAlign: 'left' }}
                          initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.4, delay: 0.3 + i * 0.08 }}
                        >
                          {child.name.toUpperCase()}
                        </motion.div>

                        <motion.div
                          style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '2px', background: '#4db0f2', zIndex: 30, scaleX: 0, originX: 0 }}
                          whileHover={{ scaleX: 1 }}
                          transition={{ duration: 0.3 }}
                        />
                      </Link>
                    </motion.div>
                  );
                })}
              </motion.div>
            )}

            {active && !imagesReady && (
              <motion.p
                key="loading"
                initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                style={{ color: '#4db0f2', fontFamily: 'Montserrat', fontSize: '0.65rem', letterSpacing: '0.22em', textTransform: 'uppercase' }}
              >
                Loading...
              </motion.p>
            )}

          </AnimatePresence>
        </div>
      </div>
    </>
  );
};

export default LandingPage;